"use client";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Notifications, type NotificationProps } from "./Notifications";
import { usePathname } from "next/navigation";
import { usePrevious } from "src/hooks/usePrevious";

type NotificationContextType = {
  clearNotification: (id: string) => void;
  createNotification: (notification: NotificationProps) => void;
};

const defaultNotificationContext = {
  clearNotification: () => {},
  createNotification: () => {},
};

const NotificationContext = createContext<NotificationContextType>(
  defaultNotificationContext,
);

export const useNotification = (): NotificationContextType => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);

  const pathname = usePathname();
  const prevPathname = usePrevious(pathname);

  const clearNotification = (id: string) =>
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );

  const createNotification = (notification: NotificationProps) =>
    setNotifications((prev) =>
      prev.find(
        (previousNotification) => previousNotification.id === notification.id,
      ) === undefined
        ? [...prev, notification]
        : prev,
    );

  // Clear notifications when the pathname changes
  useEffect(() => {
    if (pathname !== prevPathname) {
      setNotifications([]);
    }
  }, [pathname, prevPathname]);

  return (
    <NotificationContext.Provider
      value={{
        clearNotification,
        createNotification,
      }}
    >
      <Notifications notifications={notifications} />
      {children}
    </NotificationContext.Provider>
  );
};
